import { motion } from 'framer-motion'
import type { ChipValue } from '@/types'
import { cn } from '@/lib/utils'

interface ChipProps {
  value: ChipValue
  selected?: boolean
  disabled?: boolean
  onClick?: () => void
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

const chipColors: Record<ChipValue, { bg: string; text: string; border: string }> = {
  50: {
    bg: 'bg-gradient-to-br from-gray-500 to-gray-700',
    text: 'text-white',
    border: 'border-gray-400',
  },
  100: {
    bg: 'bg-gradient-to-br from-green-600 to-green-800',
    text: 'text-white',
    border: 'border-green-500',
  },
  500: {
    bg: 'bg-gradient-to-br from-blue-600 to-blue-800',
    text: 'text-white',
    border: 'border-blue-500',
  },
  1000: {
    bg: 'bg-gradient-to-br from-red-600 to-red-800',
    text: 'text-white',
    border: 'border-red-500',
  },
  5000: {
    bg: 'bg-gradient-to-br from-casino-gold-dark to-casino-gold',
    text: 'text-casino-black',
    border: 'border-casino-gold-light',
  },
}

const sizeClasses = {
  sm: 'w-10 h-10 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-16 h-16 text-base',
}

export function Chip({
  value,
  selected = false,
  disabled = false,
  onClick,
  size = 'md',
  className,
}: ChipProps) {
  const colors = chipColors[value]
  const displayValue = value >= 1000 ? `${value / 1000}K` : String(value)

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.1 } : undefined}
      whileTap={!disabled ? { scale: 0.95 } : undefined}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'rounded-full flex items-center justify-center font-bold',
        'border-4 border-dashed shadow-lg transition-all duration-200',
        sizeClasses[size],
        colors.bg,
        colors.text,
        colors.border,
        selected && 'ring-2 ring-white ring-offset-2 ring-offset-casino-black scale-105',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer hover:shadow-xl',
        className
      )}
    >
      <span className="drop-shadow-md">${displayValue}</span>
    </motion.button>
  )
}

// Chip selector component
interface ChipSelectorProps {
  selectedValue: ChipValue
  onSelect: (value: ChipValue) => void
  disabled?: boolean
}

const CHIP_VALUES: ChipValue[] = [50, 100, 500, 1000, 5000]

export function ChipSelector({ selectedValue, onSelect, disabled }: ChipSelectorProps) {
  return (
    <div className="flex gap-2 justify-center flex-wrap">
      {CHIP_VALUES.map((value) => (
        <Chip
          key={value}
          value={value}
          selected={selectedValue === value}
          disabled={disabled}
          onClick={() => onSelect(value)}
        />
      ))}
    </div>
  )
}
